import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PROVEDORES_OPENCODE,
  provedorOpencodeNaoTratado,
  type ProvedorOpencode,
} from '@remoto/protocolo';
import {
  CODIGOS_ERRO,
  ConfiguracaoOpencodeSchema,
  ESFORCO_PADRAO_OPENCODE,
  MAXIMO_DE_FAVORITOS,
  nomeDoProvedorOpencode,
  padroesDoProvedor,
  ROTAS,
  type ConfiguracaoOpencode,
  type EstadoDaConfigOpencode,
  type ModeloOpencode,
} from '@remoto/shared';
import { chamar, ErroApi } from '../api';
import { BotaoIcone, Cartao } from '../componentes/layout';
import { CampoDeEscolha } from '../componentes/campos';
import { usarConfirmacao } from '../componentes/Modal';
import { IconeAviso, IconeFechar } from '../componentes/icones';
import { mensagemDoErro } from '../erros';
import { CHAVE_DA_CONFIG, CHAVE_DO_CATALOGO, FRASES_DA_TELA, usarCatalogo } from './dados';
import { EscolhaDeModelo } from './EscolhaDeModelo';
import {
  adicionarFavorito,
  descricaoDoModelo,
  esforcoAposTrocarModelo,
  opcoesDeEsforco,
  removerFavorito,
} from './regras';

/** Um bloco da seção: título curto e, embaixo, para que serve. */
function Bloco({
  titulo,
  explicacao,
  children,
}: {
  titulo: string;
  explicacao?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="space-y-3">
      <header className="space-y-0.5">
        <h3 className="text-sm font-medium text-zinc-200">{titulo}</h3>
        {explicacao ? <p className="text-xs text-zinc-500">{explicacao}</p> : null}
      </header>
      {children}
    </section>
  );
}

/**
 * O seletor de esforço de UM modelo: "padrão do provedor" e os níveis que
 * aquele modelo aceita (R7). Modelo sem nível nenhum desliga o seletor e diz
 * por quê, em vez de oferecer uma lista de um item só.
 */
function EscolhaDeEsforco({
  rotulo,
  valor,
  modelo,
  aoEscolher,
  desabilitado = false,
}: {
  rotulo: string;
  valor: string;
  modelo: ModeloOpencode | undefined;
  aoEscolher: (esforco: string) => void;
  desabilitado?: boolean;
}): React.JSX.Element {
  const semNiveis = modelo !== undefined && modelo.esforcos.length === 0;
  return (
    <CampoDeEscolha
      rotulo={rotulo}
      valor={valor}
      opcoes={opcoesDeEsforco(modelo, valor)}
      aoEscolher={aoEscolher}
      desabilitado={desabilitado || semNiveis}
      dica={semNiveis ? 'Este modelo não tem níveis de esforço: quem decide é o provedor.' : undefined}
    />
  );
}

/**
 * A configuração com os modelos de OUTRO provedor (F2): os padrões dele, com
 * o esforço de volta ao "padrão do provedor" — o `variant` é POR MODELO, e o do
 * modelo antigo pode não existir no novo. É o que o schema exige (todo modelo
 * do provedor escolhido) e o que a decisão do dono manda (D44: os padrões do
 * NanoGPT são os seis dele).
 */
export function configDoProvedor(atual: ConfiguracaoOpencode, provedor: ProvedorOpencode): ConfiguracaoOpencode {
  const p = padroesDoProvedor(provedor);
  return {
    ...atual,
    provedor,
    modeloPadrao: p.padrao,
    esforcoPadrao: ESFORCO_PADRAO_OPENCODE,
    favoritos: [...p.favoritos],
    modeloMestre: p.mestre,
    esforcoMestre: ESFORCO_PADRAO_OPENCODE,
    modeloDeVisao: p.visao,
  };
}

/** O rodapé do cartão: o que o custo de cada turno quer dizer com o provedor ATIVO. */
function NotaDoCusto({ provedor }: { provedor: ProvedorOpencode }): React.JSX.Element {
  switch (provedor) {
    case 'nanogpt':
      /*
        D45, medido em 26/10: o OpenCode relata o preço de TABELA do modelo, e a
        chamada coberta pela assinatura não custa nada. O número do turno é o
        equivalente; o dinheiro de verdade é lido da conta do NanoGPT.
      */
      return (
        <p data-testid="nota-do-custo" className="text-[11px] text-zinc-500">
          O custo que aparece em cada turno do OpenCode é o equivalente em preço de tabela do
          NanoGPT: o turno coberto pela assinatura não sai do crédito. O dinheiro de verdade —
          saldo e gasto em crédito — está no cartão do OpenCode, na tela de Uso.
        </p>
      );
    case 'openrouter':
      /*
        R6 da Fase 0, medido: o custo que o OpenCode relata por turno é o que o
        OpenRouter cobra. Por isso a conversa pode dizer "custo real".
      */
      return (
        <p data-testid="nota-do-custo" className="text-[11px] text-zinc-500">
          O custo que aparece em cada turno do OpenCode é o custo real, relatado pelo OpenRouter. O
          total da chave se confere no painel dele.
        </p>
      );
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/**
 * O aviso dos modelos da configuração que o catálogo VIVO não tem mais (F2:
 * id do NanoGPT muda sem aviso — medido em 26/10). Eles ficam fora do que as
 * máquinas recebem, e uma sessão que os pedisse não nasceria.
 *
 * `null` é "o catálogo não respondeu agora": a tela diz "não sei", e nunca
 * "nenhum sumiu". Lista vazia não desenha nada.
 */
function ForaDoCatalogo({
  ids,
  provedor,
}: {
  ids: readonly string[] | null;
  provedor: ProvedorOpencode;
}): React.JSX.Element | null {
  const nome = nomeDoProvedorOpencode(provedor);
  if (ids === null) {
    return (
      <p data-testid="fora-do-catalogo" data-estado="nao-sei" className="text-xs text-zinc-500">
        Não deu para conferir agora se os modelos da configuração continuam no catálogo do {nome}:
        não sei se algum sumiu.
      </p>
    );
  }
  if (ids.length === 0) return null;
  const um = ids.length === 1;
  return (
    <div
      data-testid="fora-do-catalogo"
      data-estado="sumiram"
      role="status"
      className="space-y-1.5 rounded-md border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200"
    >
      <p className="flex items-start gap-1.5">
        <IconeAviso tamanho={14} className="mt-0.5" />
        {um
          ? `1 modelo da configuração não está mais no catálogo do ${nome}, e fica de fora do que as máquinas recebem:`
          : `${ids.length} modelos da configuração não estão mais no catálogo do ${nome}, e ficam de fora do que as máquinas recebem:`}
      </p>
      {/* Lista que pode crescer com a configuração: teto em `vh` e rolagem própria. */}
      <ul className="max-h-[20vh] space-y-0.5 overflow-y-auto pl-5 font-mono text-[11px] text-amber-100/90">
        {ids.map((id) => (
          <li key={id} className="break-all">
            {id}
          </li>
        ))}
      </ul>
      <p className="text-amber-300/80">
        O id de um modelo pode mudar sem aviso. Escolha o substituto abaixo e salve.
      </p>
    </div>
  );
}

/**
 * O provedor e os modelos do OpenCode da frota (D2, D12, D13, D17; R7 e R8 da
 * Fase 0; F2: o provedor).
 *
 * Um rascunho local, salvo de uma vez com `PUT`: o provedor, modelo e esforço
 * das sessões, os favoritos, modelo e esforço da mestre e o modelo de visão.
 * Trocar o modelo refaz o esforço — o nível que valia num modelo pode não
 * existir no próximo.
 *
 * **Trocar o provedor troca TODOS os modelos para os padrões do novo** (F2): o
 * schema recusa modelo de outro provedor, e o `serve` só tem a chave do ativo.
 * Enquanto a troca não é salva, a busca de modelos fica parada: ela lê o
 * catálogo do provedor ATIVO, e oferecer o do antigo para a configuração do
 * novo seria pedir o que o servidor recusa. Salvar a troca pede confirmação —
 * ela reinicia o OpenCode da frota inteira — e a API a recusa com
 * `opencode_sem_chave` quando o novo provedor ainda não tem chave guardada.
 */
export function SecaoDeModelos({ estado }: { estado: EstadoDaConfigOpencode }): React.JSX.Element {
  const { config, chaves, foraDoCatalogo } = estado;
  const cliente = useQueryClient();
  const [pedir, dialogo] = usarConfirmacao();
  const [rascunho, setRascunho] = useState<ConfiguracaoOpencode>(config);
  const [salvo, setSalvo] = useState(false);

  /*
    A config do servidor mudou (salvou aqui, ou outro admin salvou lá): o
    rascunho recomeça dela. É o ajuste de estado durante a renderização que o
    React recomenda para "estado que deriva de uma prop" — sem efeito, sem um
    quadro desenhado com o rascunho velho.
  */
  const retratoDaConfig = JSON.stringify(config);
  const [base, setBase] = useState(retratoDaConfig);
  if (base !== retratoDaConfig) {
    setBase(retratoDaConfig);
    setRascunho(config);
  }
  const mudou = JSON.stringify(rascunho) !== retratoDaConfig;
  const trocandoProvedor = rascunho.provedor !== config.provedor;
  const nomeDoNovo = nomeDoProvedorOpencode(rascunho.provedor);
  const nomeDoAtivo = nomeDoProvedorOpencode(config.provedor);
  const novoSemChave = trocandoProvedor && chaves[rascunho.provedor] === null;

  /**
   * Os modelos que a tela conhece: o catálogo inicial (favoritos primeiro) e
   * os que a pessoa escolheu por busca — um modelo achado buscando "qwen" não
   * está na lista inicial, e sem ele o favorito recém-somado apareceria só
   * com o id.
   */
  const { data: catalogo } = usarCatalogo('');
  const [vistos, setVistos] = useState<ReadonlyMap<string, ModeloOpencode>>(new Map());
  const conhecidos = useMemo(() => {
    const mapa = new Map<string, ModeloOpencode>(vistos);
    for (const m of catalogo?.modelos ?? []) mapa.set(m.id, m);
    return mapa;
  }, [catalogo, vistos]);

  function lembrar(m: ModeloOpencode): void {
    setVistos((atual) => new Map(atual).set(m.id, m));
  }

  function mudar(parcial: Partial<ConfiguracaoOpencode>): void {
    setSalvo(false);
    setRascunho((r) => ({ ...r, ...parcial }));
  }

  function escolherProvedor(novo: ProvedorOpencode): void {
    if (novo === rascunho.provedor) return;
    setSalvo(false);
    // Voltar ao provedor salvo devolve a configuração salva, e não os padrões:
    // quem desfaz a troca não quer perder a escolha que já estava gravada.
    setRascunho(novo === config.provedor ? config : configDoProvedor(rascunho, novo));
  }

  const salvar = useMutation({
    mutationFn: (corpo: ConfiguracaoOpencode) =>
      chamar(ROTAS.configuracoes.opencode, { method: 'PUT', corpo }),
    onSuccess: async () => {
      setSalvo(true);
      // O catálogo marca `favorito` a partir da config — e, se o provedor
      // trocou, ele agora é de outro provedor: envelheceu inteiro.
      await Promise.all([
        cliente.invalidateQueries({ queryKey: CHAVE_DA_CONFIG }),
        cliente.invalidateQueries({ queryKey: [CHAVE_DO_CATALOGO] }),
      ]);
    },
  });

  const valido = ConfiguracaoOpencodeSchema.safeParse(rascunho);
  const cheio = rascunho.favoritos.length >= MAXIMO_DE_FAVORITOS;
  const visao = conhecidos.get(rascunho.modeloDeVisao);
  const fora = new Set(trocandoProvedor ? [] : (foraDoCatalogo ?? []));

  function confirmarESalvar(): void {
    if (!valido.success) return;
    const corpo = valido.data;
    if (!trocandoProvedor) {
      salvar.mutate(corpo);
      return;
    }
    pedir({
      titulo: `Trocar o provedor da frota para o ${nomeDoNovo}`,
      texto: (
        <div className="space-y-2">
          <p>
            Todas as máquinas com OpenCode passam a usar o {nomeDoNovo}, com a chave dele: cada uma
            reinicia o OpenCode assim que não houver turno em andamento.
          </p>
          <p className="text-zinc-400">
            As conversas que estavam no {nomeDoAtivo} seguem no modelo padrão do {nomeDoNovo}, com
            um aviso na própria conversa. A chave do {nomeDoAtivo} fica guardada.
          </p>
        </div>
      ),
      rotuloConfirmar: `Trocar para o ${nomeDoNovo}`,
      aoConfirmar: () => salvar.mutate(corpo),
    });
  }

  /*
    O erro pelo CÓDIGO (`web.md`). O `opencode_sem_chave` desta tela é o da
    TROCA de provedor, e a frase diz qual chave falta e onde colá-la — a frase
    geral ("um ADMIN cadastra na tela de Configurações") mandaria o ADMIN para
    a tela em que ele já está.
  */
  function erroAoSalvar(e: unknown): string {
    const provedorPedido = salvar.variables?.provedor;
    if (e instanceof ErroApi && e.codigo === CODIGOS_ERRO.opencodeSemChave && provedorPedido) {
      const nome = nomeDoProvedorOpencode(provedorPedido);
      return `Falta a chave do ${nome}. Cadastre-a no cartão "Chave do ${nome}" antes de trocar o provedor: sem ela, a troca derrubaria o OpenCode da frota.`;
    }
    return mensagemDoErro(e, 'Não foi possível salvar os modelos.', FRASES_DA_TELA);
  }

  return (
    <Cartao titulo="Provedor e modelos">
      <div className="space-y-6">
        <Bloco
          titulo="Provedor"
          explicacao="Quem atende os modelos do OpenCode. Um de cada vez: o outro fica guardado, com a chave dele."
        >
          <CampoDeEscolha<ProvedorOpencode>
            rotulo="Provedor dos modelos"
            valor={rascunho.provedor}
            opcoes={PROVEDORES_OPENCODE.map((p) => {
              const chave = chaves[p];
              return {
                id: p,
                rotulo: nomeDoProvedorOpencode(p),
                descricao:
                  (p === config.provedor ? 'ativo' : 'guardado') +
                  ' · ' +
                  (chave ? `chave …${chave.final}` : 'sem chave'),
              };
            })}
            aoEscolher={escolherProvedor}
          />
          {trocandoProvedor ? (
            <div
              data-testid="troca-de-provedor"
              role="status"
              className="space-y-1.5 rounded-md border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200"
            >
              <p>
                Ao salvar, a frota passa do {nomeDoAtivo} para o {nomeDoNovo}. Os modelos abaixo
                voltaram aos padrões do {nomeDoNovo}.
              </p>
              <p className="text-amber-300/80">
                Para escolher outros modelos do {nomeDoNovo}, salve a troca primeiro: a busca é no
                catálogo do provedor ativo.
              </p>
              {novoSemChave ? (
                <p data-testid="troca-sem-chave" className="flex items-start gap-1.5 font-medium">
                  <IconeAviso tamanho={14} className="mt-0.5" />
                  Falta a chave do {nomeDoNovo}: cadastre-a no cartão dela antes, ou a troca é
                  recusada.
                </p>
              ) : null}
            </div>
          ) : null}
        </Bloco>

        {trocandoProvedor ? null : <ForaDoCatalogo ids={foraDoCatalogo} provedor={config.provedor} />}

        <Bloco
          titulo="Sessões de trabalho"
          explicacao="O que vem marcado ao criar uma sessão OpenCode. Cada sessão pode trocar depois."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <EscolhaDeModelo
              rotulo="Modelo padrão"
              valor={rascunho.modeloPadrao}
              conhecidos={conhecidos}
              desabilitado={trocandoProvedor}
              dica={descricaoOuNada(conhecidos.get(rascunho.modeloPadrao))}
              aoEscolher={(m) => {
                lembrar(m);
                mudar({
                  modeloPadrao: m.id,
                  esforcoPadrao: esforcoAposTrocarModelo(rascunho.esforcoPadrao, m),
                });
              }}
            />
            <EscolhaDeEsforco
              rotulo="Esforço padrão"
              valor={rascunho.esforcoPadrao}
              modelo={conhecidos.get(rascunho.modeloPadrao)}
              desabilitado={trocandoProvedor}
              aoEscolher={(e) => mudar({ esforcoPadrao: e })}
            />
          </div>
        </Bloco>

        <Bloco
          titulo={`Favoritos (${rascunho.favoritos.length} de ${MAXIMO_DE_FAVORITOS})`}
          explicacao="Aparecem primeiro na hora de escolher o modelo. O resto do catálogo continua na busca."
        >
          {rascunho.favoritos.length === 0 ? (
            <p className="text-sm text-zinc-500">Nenhum favorito: a escolha do modelo começa pela busca.</p>
          ) : (
            /*
              Teto em `vh` e rolagem própria: são até 30 linhas, e a lista não
              pode empurrar o botão de salvar para fora da tela do telefone.
            */
            <ul
              data-testid="lista-de-favoritos"
              className="max-h-[40vh] divide-y divide-zinc-800 overflow-y-auto rounded-md border border-zinc-800"
            >
              {rascunho.favoritos.map((id) => {
                const m = conhecidos.get(id);
                const nome = m?.nome ?? id;
                return (
                  <li key={id} data-testid="favorito" data-modelo={id} className="flex items-center gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-zinc-100">{nome}</p>
                      <p className="truncate text-[11px] text-zinc-500">
                        {id}
                        {m ? ' · ' + descricaoDoModelo(m) : ''}
                      </p>
                    </div>
                    {fora.has(id) ? (
                      <span
                        data-testid="favorito-fora-do-catalogo"
                        className="shrink-0 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-200"
                      >
                        fora do catálogo
                      </span>
                    ) : null}
                    {id === rascunho.modeloPadrao ? (
                      <span className="shrink-0 rounded bg-sky-400/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-300">
                        padrão
                      </span>
                    ) : null}
                    <BotaoIcone
                      rotulo={`Tirar ${nome} dos favoritos`}
                      onClick={() => mudar({ favoritos: removerFavorito(rascunho.favoritos, id) })}
                    >
                      <IconeFechar tamanho={14} />
                    </BotaoIcone>
                  </li>
                );
              })}
            </ul>
          )}
          <EscolhaDeModelo
            rotulo="Adicionar favorito"
            valor={null}
            vazio={cheio ? 'a lista está cheia' : 'escolha um modelo…'}
            excluir={rascunho.favoritos}
            desabilitado={cheio || trocandoProvedor}
            conhecidos={conhecidos}
            dica={cheio ? `O teto é de ${MAXIMO_DE_FAVORITOS} favoritos: tire um para somar outro.` : undefined}
            aoEscolher={(m) => {
              lembrar(m);
              mudar({ favoritos: adicionarFavorito(rascunho.favoritos, m.id) });
            }}
          />
        </Bloco>

        <Bloco
          titulo="Mestre OpenCode"
          explicacao="A mestre do OpenCode trabalha com tudo liberado, em modo fixo. O modelo dela sai daqui, e só daqui."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <EscolhaDeModelo
              rotulo="Modelo da mestre"
              valor={rascunho.modeloMestre}
              conhecidos={conhecidos}
              desabilitado={trocandoProvedor}
              dica={descricaoOuNada(conhecidos.get(rascunho.modeloMestre))}
              aoEscolher={(m) => {
                lembrar(m);
                mudar({
                  modeloMestre: m.id,
                  esforcoMestre: esforcoAposTrocarModelo(rascunho.esforcoMestre, m),
                });
              }}
            />
            <EscolhaDeEsforco
              rotulo="Esforço da mestre"
              valor={rascunho.esforcoMestre}
              modelo={conhecidos.get(rascunho.modeloMestre)}
              desabilitado={trocandoProvedor}
              aoEscolher={(e) => mudar({ esforcoMestre: e })}
            />
          </div>
        </Bloco>

        <Bloco
          titulo="Modelo de visão"
          explicacao="Um turno com imagem vai para este modelo quando a sessão está em outro — só aquele turno, e a conversa marca a troca. O catálogo nem sempre acerta quem enxerga: há modelo que diz aceitar imagem e não a vê."
        >
          <EscolhaDeModelo
            rotulo="Modelo que lê as imagens"
            valor={rascunho.modeloDeVisao}
            conhecidos={conhecidos}
            desabilitado={trocandoProvedor}
            dica={descricaoOuNada(visao)}
            aoEscolher={(m) => {
              lembrar(m);
              mudar({ modeloDeVisao: m.id });
            }}
          />
          {visao && !visao.aceitaImagem ? (
            <p className="flex items-start gap-1.5 text-xs text-amber-200">
              <IconeAviso tamanho={14} className="mt-0.5" />O catálogo não diz que este modelo aceita
              imagem.
            </p>
          ) : null}
        </Bloco>

        {/* O rodapé que decide fica fora do que rola: a lista de favoritos tem teto próprio. */}
        <div className="space-y-2 border-t border-zinc-800 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!mudou || !valido.success || salvar.isPending}
              onClick={confirmarESalvar}
              className="inline-flex items-center justify-center rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-40 pointer-coarse:min-h-11"
            >
              {salvar.isPending ? 'Salvando…' : trocandoProvedor ? `Salvar e trocar para o ${nomeDoNovo}` : 'Salvar modelos'}
            </button>
            {mudou ? (
              <button
                type="button"
                onClick={() => {
                  salvar.reset();
                  setRascunho(config);
                }}
                className="inline-flex items-center justify-center rounded-md border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 transition hover:bg-zinc-800 pointer-coarse:min-h-11"
              >
                Descartar mudanças
              </button>
            ) : null}
            {salvo && !mudou ? (
              <span role="status" className="text-sm text-emerald-300">
                Salvo.
              </span>
            ) : null}
          </div>
          {salvar.isError ? (
            <p role="alert" className="rounded-md border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-200">
              {erroAoSalvar(salvar.error)}
            </p>
          ) : null}
          <NotaDoCusto provedor={config.provedor} />
        </div>
      </div>
      {dialogo}
    </Cartao>
  );
}

function descricaoOuNada(m: ModeloOpencode | undefined): string | undefined {
  return m ? descricaoDoModelo(m) : undefined;
}
